import { useState, useRef, useEffect } from "react";
import api from "@/utils/api";
import { useBranding } from "@/contexts/BrandingContext";
import { Button } from "@/components/ui/button";
import { MessageCircle, X, Send, Trash2, Sparkles } from "lucide-react";

export default function AiAssistant() {
  const { short_name } = useBranding();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput("");
    setMessages(prev => [...prev, { id: `u-${Date.now()}-${prev.length}`, role: "user", content: userMsg }]);
    setLoading(true);

    try {
      const { data } = await api.post("/assistant/chat", { message: userMsg, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages(prev => [...prev, { id: `a-${Date.now()}-${prev.length}`, role: "assistant", content: data.response }]);
    } catch (err) {
      console.error("AI assistant chat failed:", err);
      setMessages(prev => [...prev, { id: `a-err-${Date.now()}-${prev.length}`, role: "assistant", content: "Sorry, I couldn't process that. Please try again." }]);
    }
    setLoading(false);
  };

  const clearChat = async () => {
    if (sessionId) {
      try {
        await api.delete(`/assistant/history/${sessionId}`);
      } catch (err) {
        console.error("Failed to clear chat history:", err);
      }
    }
    setMessages([]);
    setSessionId(null);
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full bg-slate-900 text-white shadow-lg hover:bg-slate-800 flex items-center justify-center transition-transform hover:scale-105"
          data-testid="ai-assistant-btn"
        >
          <MessageCircle size={22} />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[380px] max-h-[520px] bg-white border border-border rounded-lg shadow-xl flex flex-col overflow-hidden" data-testid="ai-assistant-panel">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-slate-900 text-white shrink-0">
            <div className="flex items-center gap-2">
              <Sparkles size={16} />
              <span className="font-heading font-semibold text-sm">{short_name ? `${short_name} Assistant` : "AI Assistant"}</span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={clearChat} className="p-1.5 rounded hover:bg-white/10" title="Clear chat" data-testid="clear-chat-btn"><Trash2 size={14} /></button>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded hover:bg-white/10" data-testid="close-chat-btn"><X size={14} /></button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3 min-h-[300px]" data-testid="chat-messages">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <Sparkles size={28} className="mx-auto text-slate-300 mb-3" />
                <p className="text-sm font-medium text-foreground mb-1">Hi! I'm your CRM assistant</p>
                <p className="text-xs text-muted-foreground mb-4">Ask me anything about students, courses, certificates, trainers, or finances</p>
                <div className="space-y-1.5">
                  {["Who's free to teach next week?", "How many certs are expiring this month?", "Which courses are full?"].map(q => (
                    <button key={q} onClick={() => { setInput(q); }} className="block w-full text-left text-xs px-3 py-2 rounded border border-border hover:bg-slate-50 text-muted-foreground" data-testid={`suggestion-${q.slice(0,10)}`}>
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((msg, mi) => (
              <div key={msg.id || `m-${mi}`} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                  msg.role === "user"
                    ? "bg-slate-900 text-white"
                    : "bg-slate-100 text-foreground"
                }`}>
                  {msg.role === "assistant" ? (
                    <div className="whitespace-pre-wrap leading-relaxed" style={{ fontSize: "13px" }}>
                      {(() => {
                        const mid = msg.id || msg.timestamp || "m";
                        const lines = msg.content.split("\n");
                        const out = [];
                        let i = 0;
                        const renderInline = (text, k) => text.split(/(\*\*[^*]+\*\*)/g).map((p, ki) => p.startsWith("**") && p.endsWith("**") ? <strong key={`${k}-${ki}`}>{p.slice(2, -2)}</strong> : p);
                        while (i < lines.length) {
                          const line = lines[i];
                          // Detect Markdown table: header | --- |
                          if (line.includes("|") && i + 1 < lines.length && /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?\s*$/.test(lines[i + 1])) {
                            const headers = line.split("|").map(s => s.trim()).filter(Boolean);
                            const rows = [];
                            let j = i + 2;
                            while (j < lines.length && lines[j].includes("|")) {
                              rows.push(lines[j].split("|").map(s => s.trim()).filter(Boolean));
                              j++;
                            }
                            out.push(
                              <table key={`${mid}-t-${i}`} className="w-full my-2 border border-border rounded overflow-hidden text-[12px]">
                                <thead className="bg-slate-100"><tr>{headers.map((h, hi) => <th key={`${mid}-h-${i}-${hi}`} className="px-2 py-1 text-left font-semibold">{renderInline(h, `${mid}-h-${i}-${hi}`)}</th>)}</tr></thead>
                                <tbody>{rows.map((r, ri) => <tr key={`${mid}-r-${i}-${ri}`} className="border-t border-border">{r.map((c, ci) => <td key={`${mid}-c-${i}-${ri}-${ci}`} className="px-2 py-1">{renderInline(c, `${mid}-c-${i}-${ri}-${ci}`)}</td>)}</tr>)}</tbody>
                              </table>
                            );
                            i = j;
                            continue;
                          }
                          if (line.startsWith("- ") || line.startsWith("• ")) out.push(<div key={`${mid}-l-${i}`} className="pl-2 py-0.5">{renderInline(line, `${mid}-l-${i}`)}</div>);
                          else if (line.startsWith("### ")) out.push(<p key={`${mid}-l-${i}`} className="font-semibold text-foreground mt-2">{line.slice(4)}</p>);
                          else if (line.startsWith("## ")) out.push(<p key={`${mid}-l-${i}`} className="font-bold text-foreground mt-2 text-sm">{line.slice(3)}</p>);
                          else if (line.startsWith("# ")) out.push(<p key={`${mid}-l-${i}`} className="font-bold text-foreground mt-2 text-sm">{line.slice(2)}</p>);
                          else out.push(<p key={`${mid}-l-${i}`} className={line ? "" : "h-2"}>{renderInline(line, `${mid}-l-${i}`)}</p>);
                          i++;
                        }
                        return out;
                      })()}
                    </div>
                  ) : (
                    <p style={{ fontSize: "13px" }}>{msg.content}</p>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-slate-100 rounded-lg px-3 py-2 text-sm">
                  <div className="flex items-center gap-1">
                    <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 border-t border-border shrink-0">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                placeholder="Ask anything..."
                className="flex-1 h-9 px-3 rounded-md border border-border bg-white text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                disabled={loading}
                data-testid="chat-input"
              />
              <Button size="sm" className="h-9 w-9 p-0 bg-slate-900 hover:bg-slate-800" onClick={sendMessage} disabled={loading || !input.trim()} data-testid="send-chat-btn">
                <Send size={16} />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
